/**
 * KpiDrawer - Drill-down lateral para um KPI específico.
 *
 * Slide-in da direita com:
 *  - Título + valor grande
 *  - Trend vs período anterior (só quando o KPI traz `change`)
 *  - Evolução diária dos últimos 7 dias do período, calculada a partir da
 *    série diária real do dashboard (`series`, vinda de /api/dashboard)
 *  - Link para página relacionada
 */

import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from './icons.jsx'
import { KPI_META, FALLBACK_META, buildDailySeries, fmtValue } from '../lib/kpiDrawerSeries.js'


export default function KpiDrawer({ open, kpiKey, kpi, series, onClose }) {
  const navigate = useNavigate()
  const meta = KPI_META[kpiKey] || FALLBACK_META
  const daily = useMemo(() => buildDailySeries(meta, series), [meta, series])
  const max = Math.max(0, ...daily.map((s) => s.value ?? 0))

  // Fechar com ESC + bloquear scroll do body
  useEffect(() => {
    if (!open) return
    const onEsc = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', onEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!kpi || !kpiKey) return null

  const value = typeof kpi.value === 'object' ? kpi.value.simple : kpi.value
  const label = typeof kpi.label === 'object' ? kpi.label.simple : kpi.label
  const hint = typeof kpi.hint === 'object' ? kpi.hint.simple : kpi.hint
  const hasTrend = typeof kpi.change === 'number'
  const isPositive = (kpi.change ?? 0) >= 0
  const isGood = kpi.invertTrend ? !isPositive : isPositive

  return (
    <>
      <div className={`drawer-backdrop ${open ? 'is-open' : ''}`} onClick={onClose} />
      <aside
        className={`drawer ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-label={`Detalhes de ${label}`}
        aria-modal="true"
      >
        <header className="drawer-head">
          <div className="drawer-head-title">
            <span className="drawer-icon"><Icon n={meta.icon} s={14} /></span>
            <span className="drawer-eyebrow">{label}</span>
          </div>
          <button className="drawer-close" onClick={onClose} type="button" aria-label="Fechar">
            <Icon n="x" s={16} />
          </button>
        </header>

        <div className="drawer-body">
          {/* Valor principal */}
          <section className="drawer-hero">
            <div className="drawer-hero-value">{value}</div>
            {hasTrend && (
              <div className="drawer-hero-meta">
                <span className={`trend ${isGood ? 'trend-good' : 'trend-bad'}`}>
                  <Icon n={isPositive ? 'trend_up' : 'trend_dn'} s={11} />
                  {Math.abs(kpi.change).toFixed(1).replace('.', ',')}%
                </span>
                <span className="drawer-hero-hint">vs período anterior</span>
              </div>
            )}
            <p className="drawer-hero-desc">{hint}</p>
          </section>

          {/* Mini-gráfico monocromático (últimos 7 dias do período) */}
          {daily.length > 0 && (
            <section className="drawer-section">
              <h4 className="drawer-section-title">Últimos 7 dias do período</h4>
              <div className="drawer-chart">
                {daily.map((s) => (
                  <div key={s.date} className="drawer-bar-col">
                    <div
                      className="drawer-bar"
                      style={{ height: `${max > 0 && s.value != null ? (s.value / max) * 100 : 0}%` }}
                      title={`${s.date}: ${fmtValue(s.value, meta.format)}`}
                    />
                    <span className="drawer-bar-label">{s.day}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <footer className="drawer-foot">
          <button
            className="drawer-cta"
            onClick={() => {
              navigate(meta.linkTo)
              onClose?.()
            }}
            type="button"
          >
            {meta.linkLabel}
            <Icon n="chevron_r" s={14} />
          </button>
        </footer>
      </aside>
    </>
  )
}
